import { mergeStyles, Props as ReactSelectProps } from 'react-select';
import { Control, useWatch } from 'react-hook-form';
import { formatInTimeZone } from 'date-fns-tz';
import cn from 'clsx';

import { Select, Theme } from '@bizzabo/design-system';
import { makeStyles, useTheme } from '@material-ui/styles';

import { SchedulingPreferencesFormValues } from './useSchedulingPreferencesForm/types';
import { START_HOUR_TIME, END_HOUR_TIME } from './helpers';

const leftPad = (n: number | string) => ('0' + n).slice(-2);
const timeOptionFmt = (minutes: number) => {
    const minutePart = minutes % 60;
    const hourPart = Math.floor(minutes / 60);
    const nonMidnightFmt = hourPart > 12 ? hourPart - 12 : hourPart;
    return `${hourPart === 0 ? '12' : nonMidnightFmt}:${leftPad(minutePart)}`;
};

/**
 * List of react-select options from 12:00am to 12:00pm in 15 minute intervals
 */
const END_OF_DAY_MIDNIGHT = { value: 24 * 60, label: '12:00am' };
const DAY_HOURS = Array.from(Array(24).keys());

const createOptions = (times: number[]) => {
    return times.reduce((options, hours) => {
        const minutes = hours * 60;
        const timeOfDay = hours < 12 ? 'am' : 'pm';
        return [
            ...options,
            { value: minutes, label: timeOptionFmt(minutes) + timeOfDay },
            { value: minutes + 15, label: timeOptionFmt(minutes + 15) + timeOfDay },
            { value: minutes + 30, label: timeOptionFmt(minutes + 30) + timeOfDay },
            { value: minutes + 45, label: timeOptionFmt(minutes + 45) + timeOfDay },
        ];
    }, []);
};

const createDropdownOption = (times: number[]) => {
    return createOptions(times).concat([END_OF_DAY_MIDNIGHT]);
};

const createLastDayDropdownOptions = (times: number[]) => {
    return createOptions(times);
};

export const TIME_DROPDOWN_OPTIONS = createDropdownOption(DAY_HOURS);
export interface TimeDropdownProps extends Omit<ReactSelectProps, 'options'> {
    valueTextAlign?: 'right' | 'left';
    control: Control<SchedulingPreferencesFormValues>;
    isFirstDay?: boolean;
    isLastDay?: boolean;
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({
    valueTextAlign = 'left',
    styles = {},
    className,
    item,
    isFirstDay,
    isLastDay,
    control,
    ...props
}) => {
    const classes = useStyles();
    const theme = useTheme<Theme>();
    const justifyContent = valueTextAlign === 'right' ? 'flex-end' : 'flex-start';

    const timezone = useWatch({
        name: 'timezoneId',
        control,
    });

    const getFirstDayDropdownOptions = (timezone: string) => {
        const startHour = formatInTimeZone(`${item.date}${START_HOUR_TIME}`, timezone, 'HH');
        const startHourIndex = DAY_HOURS.findIndex(hour => hour === Number(startHour));
        const relevantHours = DAY_HOURS.slice(startHourIndex);
        return createDropdownOption(relevantHours);
    };

    const getLastDayDropdownOptions = (timezone: string) => {
        const endHour = formatInTimeZone(`${item.date}${END_HOUR_TIME}`, timezone, 'HH');
        const endHourIndex = DAY_HOURS.findIndex(hour => hour === Number(endHour) + 1);
        const relevantHours = DAY_HOURS.slice(0, endHourIndex);
        return createLastDayDropdownOptions(relevantHours);
    };

    const getDropdownOptions = (timezone: string) => {
        if (isFirstDay) {
            return getFirstDayDropdownOptions(timezone);
        }
        if (isLastDay) {
            return getLastDayDropdownOptions(timezone);
        }
        return TIME_DROPDOWN_OPTIONS;
    };

    const selectTextStyling = provided => ({
        ...provided,
        ...theme.typography.t71,
        color: theme.customization.interactive.G50,
    });

    const selectStyles: ReactSelectProps['styles'] = {
        menuPortal: provided => ({ ...provided, zIndex: 10003 }),
        menu: provided => ({ ...provided, zIndex: 10003 }),
        control: provided => ({
            ...provided,
            width: 100,
            border: 0,
            background: 'transparent',
            boxShadow: 'none', // This line disables the blue border
        }),
        dropdownIndicator: provided => ({
            ...provided,
            display: 'none',
        }),
        indicatorSeparator: provided => ({
            ...provided,
            display: 'none',
        }),
        valueContainer: (provided, { hasValue }) => ({
            ...provided,
            justifyContent: hasValue ? justifyContent : provided.justifyContent,
        }),
        placeholder: selectTextStyling,
        singleValue: selectTextStyling,
    };
    return (
        <Select
            options={getDropdownOptions(timezone)}
            styles={mergeStyles(selectStyles, styles)}
            className={cn(classes.timeDropdown, className)}
            maxMenuHeight={200}
            isSearchable={false}
            menuPlacement="auto"
            placeholder="Select Time"
            {...props}
        />
    );
};
export default TimeDropdown;
const useStyles = makeStyles(
    () => ({
        timeDropdown: {
            marginBottom: 5,
        },
    }),
    { classNamePrefix: 'people-settings-scheduling-time-dropdown' }
);
